"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import type { Person } from "@/repositories/people/types";
import PlanRepository from "@/repositories/plan/plan-repository";
import type { Feature, Plan } from "@/repositories/plan/types";
import useBoundStore from "@/store/bound-store";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { shouldEnablePlanQuery } from "@/utils/plan-utils";
import { useProductionDebug } from "@/utils/production-debug";
import { PLAN_CACHE_CONFIG, PLAN_QUERY_KEY } from "./plan-constants";

const planRepository = new PlanRepository();

interface PlanContextType {
  plan: Plan | null;
  isLoading: boolean;
  isError: boolean;

  refetchPlan: (override?: Partial<Person>) => boolean;
  invalidatePlan: () => void;
  discountCredits: (feature: Feature) => Promise<void>;
  decrementLocalCredits: (feature: Feature) => void;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export function PlanProvider({ children }: { children: ReactNode }) {
  useProductionDebug("PlanProvider");
  const queryClient = useQueryClient();
  const person = useBoundStore((state) => state.person);
  const realm = usePersistedBoundStore((state) => state.realm);
  const completeProfile = person?.completeProfile;
  const planQueryKey = [...PLAN_QUERY_KEY, realm, completeProfile] as const;

  const planQuery = useQuery({
    queryKey: planQueryKey,
    queryFn: () => planRepository.getPlan(),
    staleTime: PLAN_CACHE_CONFIG.STALE_TIME,
    gcTime: PLAN_CACHE_CONFIG.GC_TIME,
    refetchInterval: PLAN_CACHE_CONFIG.REFETCH_INTERVAL,
    enabled: shouldEnablePlanQuery(completeProfile, realm),
    retry: PLAN_CACHE_CONFIG.RETRY_COUNT,
    retryDelay: PLAN_CACHE_CONFIG.RETRY_DELAY,
  });

  const discountCreditsMutation = useMutation({
    mutationFn: (feature: Feature) => planRepository.discountCreditsPlanByType(feature),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PLAN_QUERY_KEY });
    },
  });

  const plan = planQuery.data || null;
  const isLoading = planQuery.isLoading;
  const isError = planQuery.isError;

  function refetchPlan(_override?: Partial<Person>): boolean {
    const canRefetch = shouldEnablePlanQuery(completeProfile, realm);

    if (!canRefetch) {
      return false;
    }

    void planQuery.refetch();
    return true;
  }

  function invalidatePlan() {
    void queryClient.invalidateQueries({ queryKey: PLAN_QUERY_KEY });
  }

  async function discountCredits(feature: Feature) {
    await discountCreditsMutation.mutateAsync(feature);
  }

  function decrementLocalCredits(feature: Feature) {
    queryClient.setQueryData<Plan>(planQueryKey, (oldPlan) => {
      if (!oldPlan) {
        return oldPlan;
      }

      return {
        ...oldPlan,
        features: oldPlan.features.map((f) => {
          if (f.feature === feature) {
            return {
              ...f,
              availableCredits: (f.availableCredits ?? 0) - 1,
              usedCredits: f.usedCredits + 1,
            };
          }
          return f;
        }),
      };
    });
  }

  const contextValue: PlanContextType = {
    plan,
    isLoading,
    isError,
    refetchPlan,
    invalidatePlan,
    discountCredits,
    decrementLocalCredits,
  };

  return <PlanContext.Provider value={contextValue}>{children}</PlanContext.Provider>;
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (context === undefined) {
    throw new Error("usePlan must be used within a PlanProvider");
  }
  return context;
}

export function useOptionalPlan(): PlanContextType | null {
  const context = useContext(PlanContext);
  return context ?? null;
}
